import React, { useState, useEffect, useRef } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { cn } from "@/lib/utils"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  AlertCircleIcon,
  Loader2Icon,
  UploadIcon,
  UserIcon,
  XIcon,
  CheckIcon
} from "lucide-react"

interface ProfileSetupPageProps {
  /**
   * Pre-filled full name from signup
   */
  initialFullName?: string

  /**
   * Logo text or component
   */
  logo?: string | React.ReactNode

  /**
   * Current step (for multi-step flow)
   */
  currentStep?: number

  /**
   * Total steps (for multi-step flow)
   */
  totalSteps?: number

  /**
   * Heading text
   */
  heading?: string

  /**
   * Subheading text
   */
  subheading?: string

  /**
   * Redirect URL after completion
   */
  redirectTo?: string

  /**
   * Profile setup callback
   */
  onComplete?: (data: {
    fullName: string
    country: string
    timezone: string
    avatar?: File
  }) => Promise<void>

  /**
   * Skip callback
   */
  onSkip?: () => void

  /**
   * Show error state
   */
  showError?: boolean

  /**
   * Error message
   */
  errorMessage?: string

  /**
   * Additional CSS classes
   */
  className?: string

  /**
   * Devise form action URL (from ERB)
   */
  formAction?: string

  /**
   * CSRF token (from ERB)
   */
  csrfToken?: string

  /**
   * Pre-filled full name (from ERB)
   */
  fullName?: string

  /**
   * Pre-filled country (from ERB)
   */
  country?: string

  /**
   * Pre-filled timezone (from ERB)
   */
  timezone?: string

  /**
   * Pre-filled bio (from ERB)
   */
  bio?: string

  /**
   * Pre-filled LinkedIn URL (from ERB)
   */
  linkedinUrl?: string

  /**
   * Pre-filled phone (from ERB)
   */
  phone?: string

  /**
   * Server-side errors (from ERB)
   */
  errors?: string[]

  /**
   * Pre-filled designation (from ERB)
   */
  designation?: string

  /**
   * Whether user is a founder (from ERB)
   */
  isFounder?: boolean
}

// Country list (comprehensive, ASEAN first)
const COUNTRIES = [
  { value: "Malaysia", label: "Malaysia" },
  { value: "Singapore", label: "Singapore" },
  { value: "Indonesia", label: "Indonesia" },
  { value: "Thailand", label: "Thailand" },
  { value: "Philippines", label: "Philippines" },
  { value: "Vietnam", label: "Vietnam" },
  { value: "Brunei", label: "Brunei" },
  { value: "Myanmar", label: "Myanmar" },
  { value: "Cambodia", label: "Cambodia" },
  { value: "Laos", label: "Laos" },
  { value: "United States", label: "United States" },
  { value: "United Kingdom", label: "United Kingdom" },
  { value: "Canada", label: "Canada" },
  { value: "Australia", label: "Australia" },
  { value: "New Zealand", label: "New Zealand" },
  { value: "India", label: "India" },
  { value: "Bangladesh", label: "Bangladesh" },
  { value: "Sri Lanka", label: "Sri Lanka" },
  { value: "Pakistan", label: "Pakistan" },
  { value: "Nepal", label: "Nepal" },
  { value: "Japan", label: "Japan" },
  { value: "South Korea", label: "South Korea" },
  { value: "China", label: "China" },
  { value: "Hong Kong", label: "Hong Kong" },
  { value: "Taiwan", label: "Taiwan" },
  { value: "United Arab Emirates", label: "United Arab Emirates" },
  { value: "Saudi Arabia", label: "Saudi Arabia" },
  { value: "Qatar", label: "Qatar" },
  { value: "Israel", label: "Israel" },
  { value: "Turkey", label: "Turkey" },
  { value: "Egypt", label: "Egypt" },
  { value: "Nigeria", label: "Nigeria" },
  { value: "South Africa", label: "South Africa" },
  { value: "Kenya", label: "Kenya" },
  { value: "Ghana", label: "Ghana" },
  { value: "Morocco", label: "Morocco" },
  { value: "Germany", label: "Germany" },
  { value: "France", label: "France" },
  { value: "Spain", label: "Spain" },
  { value: "Italy", label: "Italy" },
  { value: "Netherlands", label: "Netherlands" },
  { value: "Sweden", label: "Sweden" },
  { value: "Norway", label: "Norway" },
  { value: "Denmark", label: "Denmark" },
  { value: "Finland", label: "Finland" },
  { value: "Poland", label: "Poland" },
  { value: "Portugal", label: "Portugal" },
  { value: "Ireland", label: "Ireland" },
  { value: "Switzerland", label: "Switzerland" },
  { value: "Austria", label: "Austria" },
  { value: "Belgium", label: "Belgium" },
  { value: "Czech Republic", label: "Czech Republic" },
  { value: "Hungary", label: "Hungary" },
  { value: "Romania", label: "Romania" },
  { value: "Greece", label: "Greece" },
  { value: "Ukraine", label: "Ukraine" },
  { value: "Russia", label: "Russia" },
  { value: "Brazil", label: "Brazil" },
  { value: "Mexico", label: "Mexico" },
  { value: "Argentina", label: "Argentina" },
  { value: "Colombia", label: "Colombia" },
  { value: "Chile", label: "Chile" },
  { value: "Peru", label: "Peru" },
  { value: "Other", label: "Other" }
]

// Timezone list (major timezones)
const TIMEZONES = [
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Anchorage", label: "Alaska Time (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii Time (HT)" },
  { value: "Europe/London", label: "London (GMT/BST)" },
  { value: "Europe/Paris", label: "Paris (CET)" },
  { value: "Europe/Berlin", label: "Berlin (CET)" },
  { value: "Europe/Rome", label: "Rome (CET)" },
  { value: "Europe/Madrid", label: "Madrid (CET)" },
  { value: "Europe/Amsterdam", label: "Amsterdam (CET)" },
  { value: "Europe/Stockholm", label: "Stockholm (CET)" },
  { value: "Europe/Oslo", label: "Oslo (CET)" },
  { value: "Europe/Copenhagen", label: "Copenhagen (CET)" },
  { value: "Europe/Helsinki", label: "Helsinki (EET)" },
  { value: "Europe/Warsaw", label: "Warsaw (CET)" },
  { value: "Europe/Lisbon", label: "Lisbon (WET)" },
  { value: "Europe/Dublin", label: "Dublin (GMT/IST)" },
  { value: "Europe/Zurich", label: "Zurich (CET)" },
  { value: "Europe/Vienna", label: "Vienna (CET)" },
  { value: "Europe/Brussels", label: "Brussels (CET)" },
  { value: "Asia/Kuala_Lumpur", label: "Kuala Lumpur (MYT)" },
  { value: "Asia/Singapore", label: "Singapore (SGT)" },
  { value: "Asia/Jakarta", label: "Jakarta (WIB)" },
  { value: "Asia/Bangkok", label: "Bangkok (ICT)" },
  { value: "Asia/Manila", label: "Manila (PHT)" },
  { value: "Asia/Ho_Chi_Minh", label: "Ho Chi Minh (ICT)" },
  { value: "Asia/Dubai", label: "Dubai (GST)" },
  { value: "Asia/Kolkata", label: "India (IST)" },
  { value: "Asia/Hong_Kong", label: "Hong Kong (HKT)" },
  { value: "Asia/Tokyo", label: "Tokyo (JST)" },
  { value: "Asia/Seoul", label: "Seoul (KST)" },
  { value: "Asia/Shanghai", label: "Shanghai (CST)" },
  { value: "Australia/Sydney", label: "Sydney (AEDT)" },
  { value: "Australia/Melbourne", label: "Melbourne (AEDT)" },
  { value: "Pacific/Auckland", label: "Auckland (NZDT)" },
  { value: "America/Sao_Paulo", label: "São Paulo (BRT)" },
  { value: "America/Mexico_City", label: "Mexico City (CST)" },
  { value: "America/Buenos_Aires", label: "Buenos Aires (ART)" },
  { value: "America/Santiago", label: "Santiago (CLT)" },
  { value: "Africa/Johannesburg", label: "Johannesburg (SAST)" },
  { value: "Europe/Istanbul", label: "Istanbul (TRT)" },
  { value: "Europe/Moscow", label: "Moscow (MSK)" }
]

// Auto-detect timezone
const detectTimezone = (): string => {
  try {
    const detected = Intl.DateTimeFormat().resolvedOptions().timeZone
    // Check if detected timezone is in our list
    const found = TIMEZONES.find(tz => tz.value === detected)
    return found ? detected : "America/New_York"
  } catch {
    return "America/New_York"
  }
}

export function ProfileSetupPage({
  initialFullName = "",
  logo = "Academician Startup",
  currentStep = 1,
  totalSteps = 1,
  heading = "Complete your profile",
  subheading = "Tell us a bit about yourself",
  redirectTo = "/home",
  onComplete,
  onSkip,
  showError: externalShowError,
  errorMessage: externalErrorMessage,
  className,
  formAction,
  csrfToken,
  fullName: erbFullName,
  country: erbCountry,
  timezone: erbTimezone,
  bio: erbBio,
  linkedinUrl: erbLinkedinUrl,
  phone: erbPhone,
  designation: erbDesignation,
  isFounder = false,
  errors
}: ProfileSetupPageProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const formRef = useRef<HTMLFormElement>(null)

  // Use ERB-provided values if available, fall back to prop defaults
  const [fullName, setFullName] = useState(erbFullName || initialFullName || "")
  const [country, setCountry] = useState(erbCountry || "")
  const [timezone, setTimezone] = useState(erbTimezone || detectTimezone())
  const [bio, setBio] = useState(erbBio || "")
  const [linkedinUrl, setLinkedinUrl] = useState(erbLinkedinUrl || "")
  const [phone, setPhone] = useState(erbPhone || "")
  const [designation, setDesignation] = useState(erbDesignation || "")
  const [avatar, setAvatar] = useState<File | null>(null)
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [internalError, setInternalError] = useState<string | null>(null)

  // Field-level errors
  const [fullNameError, setFullNameError] = useState<string | null>(null)
  const [countryError, setCountryError] = useState<string | null>(null)

  // Touched fields
  const [touched, setTouched] = useState({
    fullName: false,
    country: false
  })

  // Use server-side errors from flash, external error, or internal error
  const serverError = errors && errors.length > 0 ? errors[0] : null
  const showError = externalShowError ?? !!(serverError || internalError)
  const errorMessage = externalErrorMessage ?? serverError ?? internalError

  // Real-time validation
  useEffect(() => {
    if (touched.fullName) {
      if (!fullName.trim()) {
        setFullNameError("Full name is required")
      } else if (fullName.trim().length < 2) {
        setFullNameError("Name must be at least 2 characters")
      } else {
        setFullNameError(null)
      }
    }
  }, [fullName, touched.fullName])

  useEffect(() => {
    if (touched.country) {
      if (!country) {
        setCountryError("Please select your country")
      } else {
        setCountryError(null)
      }
    }
  }, [country, touched.country])

  const handleBlur = (field: keyof typeof touched) => {
    setTouched(prev => ({ ...prev, [field]: true }))
  }

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        setInternalError("Please upload an image file")
        return
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setInternalError("Image size must be less than 5MB")
        return
      }

      setAvatar(file)
      
      // Create preview
      const reader = new FileReader()
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string)
      }
      reader.readAsDataURL(file)
      
      setInternalError(null)
    }
  }

  const handleRemoveAvatar = () => {
    setAvatar(null)
    setAvatarPreview(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setInternalError(null)

    // Mark all fields as touched
    setTouched({
      fullName: true,
      country: true
    })

    // Validate required fields
    const errors: string[] = []

    if (!fullName.trim()) {
      setFullNameError("Full name is required")
      errors.push("Full name is required")
    } else if (fullName.trim().length < 2) {
      setFullNameError("Name must be at least 2 characters")
      errors.push("Name must be at least 2 characters")
    }

    if (!country) {
      setCountryError("Please select your country")
      errors.push("Please select your country")
    }

    if (errors.length > 0) {
      setInternalError("Please fill in all required fields")
      return
    }

    setIsLoading(true)

    // If formAction is provided (from ERB), submit as a real form POST
    if (formAction) {
      formRef.current?.submit()
      return
    }

    try {
      if (onComplete) {
        await onComplete({
          fullName: fullName.trim(),
          country,
          timezone,
          avatar: avatar || undefined
        })
      }

      // Redirect after successful completion
      window.location.href = redirectTo
    } catch (error) {
      setInternalError(
        error instanceof Error ? error.message : "An error occurred. Please try again."
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleSkip = () => {
    if (onSkip) {
      onSkip()
    } else {
      window.location.href = redirectTo
    }
  }

  const progressPercentage = (currentStep / totalSteps) * 100

  return (
    <div className={cn("min-h-screen bg-muted/30 flex items-center justify-center p-4", className)}>
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="bg-card border border-border rounded-lg shadow-lg p-8 space-y-6">
          {/* Logo */}
          <div className="text-center">
            {typeof logo === "string" ? (
              <h1 className="text-3xl font-bold text-[#666666]">{logo}</h1>
            ) : (
              logo
            )}
          </div>

          {/* Progress Indicator */}
          {totalSteps > 1 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  Step {currentStep} of {totalSteps}
                </span>
                <span className="text-muted-foreground font-medium">
                  {Math.round(progressPercentage)}%
                </span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-300"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>
          )}

          {/* Heading */}
          <div className="text-center space-y-2">
            <h2 className="text-2xl font-bold text-foreground">{heading}</h2>
            <p className="text-sm text-muted-foreground">{subheading}</p>
          </div>

          {/* Error Alert */}
          {showError && errorMessage && (
            <Alert variant="destructive" className="bg-destructive/10 border-destructive/50">
              <AlertCircleIcon className="h-4 w-4" />
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}

          {/* Form */}
          <form ref={formRef} onSubmit={handleSubmit} action={formAction} method="post" className="space-y-5">
            {csrfToken && <input type="hidden" name="authenticity_token" value={csrfToken} />}
            {formAction && <input type="hidden" name="_method" value="patch" />}
            {/* Hidden inputs for Select-based fields (Select components don't have name attr) */}
            <input type="hidden" name="user[country]" value={country} />
            <input type="hidden" name="user[timezone]" value={timezone} />
            {isFounder && <input type="hidden" name="user[designation]" value={designation} />}
            {/* Avatar Upload */}
            <div className="space-y-2">
              <Label>Profile Picture (Optional)</Label>
              <div className="flex items-center gap-4">
                {/* Avatar Preview */}
                <div className="relative">
                  <div className="w-20 h-20 rounded-full bg-muted border-2 border-border overflow-hidden flex items-center justify-center">
                    {avatarPreview ? (
                      <img
                        src={avatarPreview}
                        alt="Avatar preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <UserIcon className="w-8 h-8 text-muted-foreground" />
                    )}
                  </div>
                  {avatarPreview && (
                    <button
                      type="button"
                      onClick={handleRemoveAvatar}
                      className="absolute -top-1 -right-1 w-6 h-6 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center hover:bg-destructive/90 transition-colors"
                      disabled={isLoading}
                    >
                      <XIcon className="w-3 h-3" />
                    </button>
                  )}
                </div>

                {/* Upload Button */}
                <div className="flex-1">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarChange}
                    className="hidden"
                    disabled={isLoading}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isLoading}
                    className="w-full"
                  >
                    <UploadIcon className="w-4 h-4 mr-2" />
                    {avatarPreview ? "Change Photo" : "Upload Photo"}
                  </Button>
                  <p className="text-xs text-muted-foreground mt-1">
                    JPG, PNG or GIF. Max 5MB.
                  </p>
                </div>
              </div>
            </div>

            {/* Full Name Field */}
            <div className="space-y-2">
              <Label htmlFor="fullName">
                Full Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="fullName"
                name="user[full_name]"
                type="text"
                placeholder="John Doe"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                onBlur={() => handleBlur("fullName")}
                disabled={isLoading}
                className={cn(
                  fullNameError && touched.fullName && "border-destructive focus-visible:ring-destructive"
                )}
              />
              {fullNameError && touched.fullName && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircleIcon className="w-3 h-3" />
                  {fullNameError}
                </p>
              )}
            </div>

            {/* Country Field */}
            <div className="space-y-2">
              <Label htmlFor="country">
                Country <span className="text-destructive">*</span>
              </Label>
              <Select
                value={country}
                onValueChange={(value) => {
                  setCountry(value)
                  handleBlur("country")
                }}
                disabled={isLoading}
              >
                <SelectTrigger
                  id="country"
                  className={cn(
                    countryError && touched.country && "border-destructive focus:ring-destructive"
                  )}
                >
                  <SelectValue placeholder="Select your country" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {COUNTRIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {countryError && touched.country && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircleIcon className="w-3 h-3" />
                  {countryError}
                </p>
              )}
            </div>

            {/* Timezone Field */}
            <div className="space-y-2">
              <Label htmlFor="timezone">Timezone</Label>
              <Select
                value={timezone}
                onValueChange={setTimezone}
                disabled={isLoading}
              >
                <SelectTrigger id="timezone">
                  <SelectValue placeholder="Select your timezone" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {TIMEZONES.map((tz) => (
                    <SelectItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <CheckIcon className="w-3 h-3" />
                Auto-detected from your browser
              </p>
            </div>

            {/* Designation Field (Founder only) */}
            {isFounder && (
              <div className="space-y-2">
                <Label htmlFor="designation">
                  Designation <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={designation}
                  onValueChange={setDesignation}
                  disabled={isLoading}
                >
                  <SelectTrigger id="designation">
                    <SelectValue placeholder="Select your designation" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ceo">CEO</SelectItem>
                    <SelectItem value="cto">CTO</SelectItem>
                    <SelectItem value="ct">CT</SelectItem>
                    <SelectItem value="tt">TT</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground italic">
                  TT = Technical Team, CT = Commercial Team
                </p>
              </div>
            )}

            {/* Bio Field (Optional) */}
            <div className="space-y-2">
              <Label htmlFor="bio">Bio (Optional)</Label>
              <textarea
                id="bio"
                name="user[bio]"
                placeholder="Tell us about yourself..."
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                disabled={isLoading}
                rows={3}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>

            {/* LinkedIn URL Field (Optional) */}
            <div className="space-y-2">
              <Label htmlFor="linkedinUrl">LinkedIn URL (Optional)</Label>
              <Input
                id="linkedinUrl"
                name="user[linkedin_url]"
                type="url"
                placeholder="https://linkedin.com/in/yourprofile"
                value={linkedinUrl}
                onChange={(e) => setLinkedinUrl(e.target.value)}
                disabled={isLoading}
              />
            </div>

            {/* Phone Field (Optional) */}
            <div className="space-y-2">
              <Label htmlFor="phone">Phone (Optional)</Label>
              <Input
                id="phone"
                name="user[phone]"
                type="tel"
                placeholder="+1 (555) 000-0000"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={isLoading}
              />
            </div>

            {/* Complete Setup Button */}
            <Button
              type="submit"
              className="w-full"
              size="lg"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2Icon className="w-4 h-4 mr-2 animate-spin" />
                  Setting up...
                </>
              ) : (
                "Complete Setup"
              )}
            </Button>

            {/* Skip Link */}
            <div className="text-center">
              <button
                type="button"
                onClick={handleSkip}
                disabled={isLoading}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors underline-offset-4 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Skip for now
              </button>
            </div>
          </form>
        </div>

        {/* Helper Text */}
        <p className="text-center text-xs text-muted-foreground mt-4">
          You can always update your profile later in settings
        </p>
      </div>
    </div>
  )
}